<template>
  <div class="exception-table-wrapper">
    <!-- Filters Row - 100% Aligned 6 Columns -->
    <v-row dense class="filter-row mb-3 pb-3" align="end">
      <!-- 1. Workspace -->
      <v-col cols="12" sm="4" md="2" class="filter-col">
        <v-label class="filter-label">Workspace</v-label>
        <AutoComplete
          :multiple="true"
          :clearable="true"
          :hide-details="true"
          :items="backupWorkspace"
          placeholder="Workspace"
          item-title="name"
          item-value="id"
          v-model="formData.workSpace_id"
          @update:modelValue="changeWorkSpaceData"
        />
      </v-col>

      <!-- 2. Account -->
      <v-col cols="12" sm="4" md="2" class="filter-col">
        <v-label class="filter-label">Account</v-label>
        <AutoComplete
          :multiple="true"
          :clearable="true"
          :hide-details="true"
          :items="backupAccount"
          placeholder="Account"
          item-title="name"
          item-value="id"
          v-model="formData.account_id"
          @update:modelValue="changeFormData"
        />
      </v-col>

      <!-- 3. Created (Defaults to today's date, user can change or pick range) -->
      <v-col cols="12" sm="4" md="2" class="filter-col1">
        <v-label class="filter-label">Created</v-label>
        <DatePicker
          @update:modelValue="selectDate($event)"
          :data="formData.created"
          class="pb-0"
          selectionMode="range"
          placeholder="Date"
          @selectDate="selectDate($event)"
        />
      </v-col>

      <!-- 4. Stage (Required) -->
      <v-col cols="12" sm="4" md="2" class="filter-col">
        <v-label class="filter-label">
          Stage <span class="required-star">*</span>
        </v-label>
        <AutoComplete
          :multiple="true"
          :clearable="true"
          :hide-details="true"
          :items="stageData"
          placeholder="Stage"
          :disabled="showReadOnly"
          item-title="name"
          item-value="code"
          v-model="formData.stage_id"
          @update:modelValue="changeStage"
        />
      </v-col>

      <!-- 5. Report Name -->
      <v-col cols="12" sm="4" md="2" class="filter-col">
        <v-label class="filter-label">Report Name</v-label>
        <AutoComplete
          :multiple="true"
          :clearable="true"
          :hide-details="true"
          :items="backupReport"
          placeholder="Subcategory"
          :disabled="showReadOnly"
          item-title="name"
          item-value="code"
          v-model="formData.report_code"
          @update:modelValue="changeReport"
        />
      </v-col>

      <!-- 6. Clear Filters Button - Perfectly Aligned -->
      <v-col cols="12" sm="4" md="2" class="filter-col">
        <div class="filter-label-spacer">&nbsp;</div>
        <v-btn
          color="error"
          variant="tonal"
          class="w-100 filter-clear-btn"
          height="40"
          prepend-icon="mdi-filter-off-outline"
          @click="clearFilter"
        >
          Clear Filters
        </v-btn>
      </v-col>
    </v-row>

    <!-- Stage and Summary Info Bar -->
    <div class="stage-info-bar mb-3">
      <div class="stage-badge-container">
        <span class="stage-info-text">{{ getShowStages }}</span>
      </div>
      <div class="count-summary">
        <span class="count-chip mr-2">
          <strong>{{ accountData ? accountData.length : 0 }}</strong> Accounts
        </span>
        <span class="count-chip">
          <strong>{{ subCategoryService ? subCategoryService.length : 0 }}</strong> Reports
        </span>
      </div>
    </div>

    <!-- Full-Width Exception Table Container -->
    <div class="table-card-wrapper">
      <!-- Loading Overlay -->
      <div v-if="tableLoading" class="table-loader-overlay">
        <v-progress-circular
          indeterminate
          color="primary"
          size="46"
          width="4"
        ></v-progress-circular>
        <span class="table-loader-text mt-3">Loading report data...</span>
      </div>

      <div class="table-container">
        <table class="exception-table">
          <thead>
            <tr>
              <th class="first-column account-col">Account</th>
              <th class="first-column created-col">Created</th>
              <th class="first-column stage-col">Stage</th>
              <th
                class="headerElem subcategory-col"
                v-for="value in subCategoryService"
                :key="value.id"
              >
                <div class="header-content">
                  <span class="header-text" :title="value && value.name ? value.name : ''">
                    {{ value && value.name ? getSortName(value.name) : "" }}
                  </span>
                </div>
              </th>
              <th class="headerElem total-column total-header-cell">
                <div class="header-content total-header-content">
                  <span class="header-text total-header-text">Total</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="scrollable-body">
            <tr v-if="!tableLoading && (!accountData || accountData.length === 0)">
              <td
                :colspan="3 + (subCategoryService ? subCategoryService.length : 0) + 1"
                class="no-data-cell"
              >
                <div class="no-data-content">
                  <v-icon size="36" color="#94a3b8" class="mb-2">mdi-clipboard-text-off-outline</v-icon>
                  <div class="no-data-title">No Data Found</div>
                  <div class="no-data-subtitle">There are no records matching your selected filter.</div>
                </div>
              </td>
            </tr>
            <tr v-for="account in accountData" :key="account.id || account.name">
            <td class="account-cell" :title="account.name">
              <span class="account-name-text">{{ account.name }}</span>
            </td>
            <td class="created-cell" :title="getCreatedDate">
              <div class="workspace-cell">
                {{ getCreatedDate }}
              </div>
            </td>
            <td class="stage-cell">
              <div
                class="workspace-cell stage-cell-wrapper"
                :title="
                  formData && formData.stage_id && formData.stage_id.length > 0
                    ? formData.stage_id.join(', ')
                    : 'Open'
                "
              >
                <span class="stage-cell-text">
                  {{
                    formData && formData.stage_id && formData.stage_id.length > 0
                      ? formData.stage_id.join(', ')
                      : 'Open'
                  }}
                </span>
              </div>
            </td>
            <td
              class="subcategory-cell"
              v-for="subItem in subCategoryService"
              :key="subItem.id"
            >
              <div
                class="workspace-cell count-cell"
                @click="openIssues(account, subItem)"
              >
                {{ getTheCount(account, subItem) }}
              </div>
            </td>
            <td
              class="total-column-cell total-data-cell"
              @click="openIssues(account, null)"
            >
              <span class="total-count-text">
                {{ account ? getAccountTotal(account) : 0 }}
              </span>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="total-row">
            <td class="total-column-cell footer-account">Total</td>
            <td class="total-column-cell footer-created"></td>
            <td class="total-column-cell footer-stage"></td>
            <td
              class="total-column-cell footer-subitem"
              v-for="item in subCategoryService"
              :key="item.id"
            >
              <span
                class="footer-count-text"
                @click="openIssues(null, item)"
              >
                {{ getSubCategoryTotal(item) }}
              </span>
            </td>
            <td class="total-column-cell footer-grand-total">
              <span
                class="footer-grand-total-text"
                @click="openIssues(null, null)"
              >
                {{ getTotal() }}
              </span>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>

    <!-- Issue Details Navigation Drawer -->
    <v-layout>
      <v-navigation-drawer
        :width="600"
        v-model="drawer"
        style="margin-top: 70px"
        location="right"
        permanent
      >
        <workSpaceView
          ref="workspaceView"
          @closedrawer="drawer = false"
        />
      </v-navigation-drawer>
    </v-layout>
  </div>
</template>

<script>
import DashboardService from "@/api/service/Dashboard/DashboardService.js";
import CategoryService from "@/api/service/Category/CategoryService";
import SubCategoryService from "@/api/service/SubCategory/SubCategoryService";
import AccountService from "@/api/service/Account/AccountService";
import WorkSpaceService from "@/api/service/WorkSpace/WorkSpaceService";
import AutoComplete from "@/components/ui/AutoComplete.vue";
import DatePicker from "@/components/ui/DatePicker.vue";
import { getStageData } from "./ExceptionData";
import commonService from "@/common.service";
import moment from "moment";
import workSpaceView from "./workSpaceView.vue";

export default {
  components: {
    AutoComplete,
    DatePicker,
    workSpaceView,
  },
  data() {
    return {
      tableLoading: false,
      data: [],
      countMap: {},
      accountTotalMap: {},
      subCategoryTotalMap: {},
      grandTotal: 0,
      fetchTimer: null,
      filterTimer: null,
      workspaceTimer: null,
      reportTimer: null,
      stageTimer: null,
      drawer: false,
      sortId: null,
      formData: {
        stage_id: ["open"],
        created: null,
        workSpace_id: [],
        account_id: [],
        report_code: [],
      },
      categoryData: [],
      subCategoryService: [],
      accountData: [],
      backupAccount: [],
      backupWorkspace: [],
      backupReport: [],
      open: ["open", "pending", "assigned", "inprogress"],
      closed: ["closed", "completed"],
      cancel: ["cancel"],
      stageData: [],
      showStages: null,
    };
  },
  async created() {
    this.tableLoading = true;
    this.formData.stage_id = ["open"];
    this.formData.created = null;
    this.formData.workSpace_id = [];
    this.formData.account_id = [];
    this.formData.report_code = [];
    this.getAccountData();
    this.getWorkspaceData();
    this.getcategoryData();
    this.stageData = getStageData();
    this.getSubCategoryService();
    this.getExceptionData();
  },
  computed: {
    getShowStages() {
      if (
        !this.formData ||
        !this.formData.stage_id ||
        this.formData.stage_id.length === 0
      ) {
        let allStages = this.open.concat(this.closed, this.cancel);
        return `All Stages: [${allStages.join(", ")}]`;
      }
      let stages = this.stageOpenIssue();
      let stagesStr = stages && stages.length > 0 ? stages.join(", ") : "";
      let stageId = this.formData.stage_id;
      let hasOpen = stageId.includes("open");
      let hasClosed = stageId.includes("closed");
      let hasCancel = stageId.includes("cancel");

      if (hasOpen && hasClosed && hasCancel) {
        return `Open & Closed & Cancel: [${stagesStr}]`;
      } else if (hasOpen && hasClosed) {
        return `Open & Closed: [${stagesStr}]`;
      } else if (hasOpen && hasCancel) {
        return `Open & Cancel: [${stagesStr}]`;
      } else if (hasClosed && hasCancel) {
        return `Closed & Cancel: [${stagesStr}]`;
      } else if (hasOpen) {
        return `Open: [${stagesStr}]`;
      } else if (hasClosed) {
        return `Closed: [${stagesStr}]`;
      } else if (hasCancel) {
        return `Cancel: [${stagesStr}]`;
      }
      return `Stages: [${stagesStr}]`;
    },
    getCreatedDate() {
      const parseMoment = (val) => {
        if (!val) return null;
        if (val instanceof Date) {
          const m = moment(val);
          return m.isValid() ? m : null;
        }
        if (typeof val === "string") {
          let m = moment(val, ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", moment.ISO_8601], true);
          if (m.isValid()) return m;
          m = moment(val);
          return m.isValid() ? m : null;
        }
        const m = moment(val);
        return m.isValid() ? m : null;
      };

      if (this.formData && this.formData.created) {
        if (Array.isArray(this.formData.created)) {
          const d0 = parseMoment(this.formData.created[0]);
          const d1 = parseMoment(this.formData.created[1]);
          if (d0 && d1) {
            return `${d0.format("DD-MM-YYYY")} - ${d1.format("DD-MM-YYYY")}`;
          } else if (d0) {
            return d0.format("DD-MM-YYYY");
          }
        } else {
          const m = parseMoment(this.formData.created);
          if (m) {
            return m.format("DD-MM-YYYY");
          }
        }
      }
      return moment().format("DD-MM-YYYY");
    },
  },
  methods: {
    moment,
    buildCountMaps() {
      const countMap = {};
      const accountTotalMap = {};
      const subCategoryTotalMap = {};
      let grandTotal = 0;

      const rawData = (this.data && this.data.data) ? this.data.data : [];
      const activeStages = (this.formData.stage_id && this.formData.stage_id.length > 0)
        ? this.formData.stage_id
        : ["open", "closed", "cancel"];

      for (let i = 0; i < rawData.length; i++) {
        const row = rawData[i];
        let rowCount = 0;
        for (let j = 0; j < activeStages.length; j++) {
          rowCount += (row[activeStages[j]] || 0);
        }
        if (rowCount > 0) {
          const key = `${row.account_id}_${row.sub_category_id}`;
          countMap[key] = (countMap[key] || 0) + rowCount;
          accountTotalMap[row.account_id] = (accountTotalMap[row.account_id] || 0) + rowCount;
          subCategoryTotalMap[row.sub_category_id] = (subCategoryTotalMap[row.sub_category_id] || 0) + rowCount;
          grandTotal += rowCount;
        }
      }

      this.countMap = countMap;
      this.accountTotalMap = accountTotalMap;
      this.subCategoryTotalMap = subCategoryTotalMap;
      this.grandTotal = grandTotal;
    },
    clearFilter() {
      // Stage is NOT removed on clear button click - preserves selected stage or defaults to ['open']
      const currentStage = (this.formData.stage_id && this.formData.stage_id.length > 0)
        ? [...this.formData.stage_id]
        : ["open"];

      this.formData = {
        workSpace_id: [],
        account_id: [],
        created: null,
        stage_id: currentStage,
        report_code: [],
      };
      this.tableLoading = true;
      this.accountData = this.backupAccount;
      this.subCategoryService = [...this.backupReport];
      this.getExceptionData();

      // Reset scroll position to top-left
      this.$nextTick(() => {
        const container = this.$el?.querySelector?.(".table-container");
        if (container) {
          container.scrollLeft = 0;
          container.scrollTop = 0;
        }
      });
    },
    changeStage() {
      if (this.stageTimer) clearTimeout(this.stageTimer);
      this.stageTimer = setTimeout(() => {
        this.buildCountMaps();
      }, 100);
    },
    debouncedGetExceptionData() {
      if (this.fetchTimer) clearTimeout(this.fetchTimer);
      this.fetchTimer = setTimeout(() => {
        this.getExceptionData();
      }, 250);
    },
    stageOpenIssue() {
      if (
        !this.formData ||
        !this.formData.stage_id ||
        this.formData.stage_id.length === 0
      ) {
        return this.open.concat(this.closed, this.cancel);
      }
      let stageId = this.formData.stage_id;
      let hasOpen = stageId.includes("open");
      let hasClosed = stageId.includes("closed");
      let hasCancel = stageId.includes("cancel");

      if (hasOpen && hasClosed && hasCancel) {
        return this.open.concat(this.closed, this.cancel);
      } else if (hasOpen && hasClosed) {
        return this.open.concat(this.closed);
      } else if (hasOpen && hasCancel) {
        return this.open.concat(this.cancel);
      } else if (hasClosed && hasCancel) {
        return this.closed.concat(this.cancel);
      } else if (hasOpen) {
        return this.open;
      } else if (hasClosed) {
        return this.closed;
      } else if (hasCancel) {
        return this.cancel;
      }
      return this.open;
    },
    async openIssues(aData, subData) {
      this.drawer = true;
      if (this.$refs.workspaceView) {
        this.$refs.workspaceView.createdData = this.formData.created;
        this.$refs.workspaceView.stage = (this.formData.stage_id && this.formData.stage_id.length > 0)
          ? this.formData.stage_id
          : ["open"];
        this.$refs.workspaceView.loader = true;
        this.$refs.workspaceView.issueType = [];
        this.$refs.workspaceView.filterData = false;
        this.$refs.workspaceView.searchQuery = "";
        this.$refs.workspaceView.searchClicked = false;
      }
      let obj = {};
      if (this.formData.created) {
        obj.created = Array.isArray(this.formData.created)
          ? JSON.stringify(this.formData.created)
          : JSON.stringify([moment(this.formData.created).format("YYYY-MM-DD")]);
      } else {
        obj.created = JSON.stringify([]);
      }
      obj.stage = this.stageOpenIssue();
      obj.category_id = 88;
      if (aData && aData.id) {
        obj.account_id = aData.id;
      }
      if (subData && subData.id) {
        obj.sub_category_id = subData.id;
      }
      try {
        let res = await DashboardService.getExceptionIssues(obj);
        if (this.$refs.workspaceView) {
          this.$refs.workspaceView.issueType = res && res.length > 0 ? res : [];
          this.$refs.workspaceView.originalIssueType = res && res.length > 0 ? [...res] : [];
          this.$refs.workspaceView.createdData = this.formData.created;
          this.$refs.workspaceView.stage = (this.formData.stage_id && this.formData.stage_id.length > 0)
            ? this.formData.stage_id
            : ["open"];
        }
      } catch (err) {
        console.error("Error fetching exception issues:", err);
        if (this.$refs.workspaceView) {
          this.$refs.workspaceView.issueType = [];
        }
      } finally {
        if (this.$refs.workspaceView) {
          this.$refs.workspaceView.loader = false;
        }
      }
    },
    async getcategoryData() {
      let res = await CategoryService.findAll({ active: 1 });
      this.categoryData =
        res && res.length > 0
          ? await commonService.getSort(res, "name", "asc")
          : [];
    },
    async getSubCategoryService() {
      let res = await SubCategoryService.findAll({
        active: 1,
        category_id: 88,
      });
      this.subCategoryService =
        res && res.length > 0
          ? await commonService.getSort(res, "name", "asc")
          : [];
      this.backupReport = [...this.subCategoryService];
    },
    async getAccountData() {
      let res = await AccountService.findAll({ active: 1 });
      this.backupAccount =
        res && res.length > 0
          ? await commonService.getSort(res, "name", "asc")
          : [];
      this.accountData = this.backupAccount;
    },
    async getWorkspaceData() {
      let res = await WorkSpaceService.findAll({ active: 1 });
      this.backupWorkspace =
        res && res.length > 0
          ? await commonService.getSort(res, "name", "asc")
          : [];
    },
    selectDate($event) {
      this.formData.created = $event || null;
      this.tableLoading = true;
      this.debouncedGetExceptionData();
    },
    async getExceptionData() {
      this.tableLoading = true;
      try {
        let res = await DashboardService.getExceptionData(this.formData);
        this.data = res && res.data && res.data.data ? res.data.data : {};
        if (this.data && this.data.data && this.data.data.length > 0) {
          for (let i = 0; i < this.data.data.length; i++) {
            this.data.data[i].open =
              (this.data.data[i]["open"] || 0) +
              (this.data.data[i]["pending"] || 0) +
              (this.data.data[i]["assigned"] || 0) +
              (this.data.data[i]["inprogress"] || 0);
            this.data.data[i].closed =
              (this.data.data[i]["closed"] || 0) +
              (this.data.data[i]["completed"] || 0);
          }
        }
        this.buildCountMaps();
      } catch (err) {
        console.error("Error fetching exception data:", err);
      } finally {
        this.tableLoading = false;
      }
    },
    getTheCount(aData, sData) {
      if (!aData || !sData || !this.countMap) return "-";
      const val = this.countMap[`${aData.id}_${sData.id}`];
      return (val !== undefined && val > 0) ? val : "-";
    },
    getSubCategoryTotal(item) {
      if (!item || !this.subCategoryTotalMap) return "-";
      const val = this.subCategoryTotalMap[item.id];
      return (val !== undefined && val > 0) ? val : "-";
    },
    getAccountTotal(item) {
      if (!item || !this.accountTotalMap) return 0;
      return this.accountTotalMap[item.id] || 0;
    },
    getTotal() {
      return this.grandTotal || 0;
    },
    changeFormData() {
      if (this.filterTimer) clearTimeout(this.filterTimer);
      this.tableLoading = true;
      this.filterTimer = setTimeout(() => {
        let accIds = this.formData.account_id || [];
        if (!Array.isArray(accIds)) accIds = accIds ? [accIds] : [];
        if (accIds.length > 0) {
          this.accountData = this.backupAccount.filter((a) => accIds.includes(a.id));
        } else {
          this.accountData = this.backupAccount;
        }
        this.getExceptionData();
      }, 150);
    },
    async changeWorkSpaceData() {
      if (this.workspaceTimer) clearTimeout(this.workspaceTimer);
      this.tableLoading = true;
      this.workspaceTimer = setTimeout(() => {
        let wIds = this.formData.workSpace_id || [];
        if (!Array.isArray(wIds)) wIds = wIds ? [wIds] : [];
        if (wIds.length === 0) {
          this.accountData = this.backupAccount;
          this.getExceptionData();
          return;
        }
        let accId = [];
        this.backupWorkspace.forEach((wDt) => {
          if (wIds.includes(wDt.id)) {
            let aId =
              typeof wDt.account_id === "string"
                ? JSON.parse(wDt.account_id)
                : wDt.account_id || [];
            accId = [...accId, ...aId];
          }
        });
        if (accId.length > 0) {
          this.accountData = this.backupAccount.filter((a) => accId.includes(a.id));
        } else {
          this.accountData = this.backupAccount;
        }
        this.getExceptionData();
      }, 150);
    },
    async changeReport() {
      if (this.reportTimer) clearTimeout(this.reportTimer);
      this.reportTimer = setTimeout(() => {
        let rCodes = this.formData.report_code || [];
        if (!Array.isArray(rCodes)) rCodes = rCodes ? [rCodes] : [];
        if (rCodes.length > 0) {
          this.subCategoryService = this.backupReport.filter((r) => rCodes.includes(r.code));
        } else {
          this.subCategoryService = [...this.backupReport];
        }
      }, 100);
    },
    getSortName(name) {
      if (name && name.length > 22) {
        return `${name.slice(0, 19)}...`;
      }
      return name || "";
    },
  },
};
</script>

<style scoped>
/* Container & Layout */
.exception-table-wrapper {
  width: 100%;
}

.filter-row {
  width: 100%;
  margin: 0 !important;
}

.filter-col {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.filter-label {
  font-size: 13px;
  color: #cf0707;
  font-weight: 700;
  display: block;
  line-height: 18px;
  height: 18px;
  margin-bottom: 4px;
}

.filter-label-spacer {
  font-size: 13px;
  line-height: 18px;
  height: 18px;
  margin-bottom: 4px;
  user-select: none;
  visibility: hidden;
}

.required-star {
  color: #cf0707;
  padding-left: 2px;
  font-weight: 700;
}

.filter-clear-btn {
  font-weight: 600;
  text-transform: none;
  letter-spacing: normal;
  height: 40px !important;
}

/* Stage Info Bar */
.stage-info-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 12px;
  background: #f7f9fb;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.stage-info-text {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.count-summary {
  display: flex;
  align-items: center;
}

.count-chip {
  font-size: 13px;
  color: #64748b;
  background: #ffffff;
  padding: 2px 10px;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
}

/* Table Container & Full Width with Visible Scrollbars */
.table-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: auto;
  max-height: calc(100vh - 250px);
  min-height: 360px;
  border: 1px solid #d5e0e8;
  border-radius: 6px;
  background: #ffffff;
  position: relative;
  scrollbar-width: auto;
  scrollbar-color: #64748b #f1f5f9;
}

/* Explicitly Visible Scrollbars for Exception Table */
.table-container::-webkit-scrollbar {
  height: 15px !important;
  width: 10px !important;
  display: block !important;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f5f9 !important;
  border-radius: 8px !important;
}

.table-container::-webkit-scrollbar-thumb {
  background: #64748b !important;
  border-radius: 8px !important;
  border: 2px solid #f1f5f9 !important;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #475569 !important;
}

.exception-table {
  border-collapse: separate;
  border-spacing: 0;
  white-space: nowrap;
  width: max-content;
  min-width: 100%;
  table-layout: fixed;
}

/* Headers */
thead {
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 9;
}

.headerElem {
  height: 145px;
  min-width: 44px;
  max-width: 44px;
  width: 44px;
  padding: 8px 2px;
  border-right: 1px solid #dbe5ec;
  border-bottom: 2px solid #cbd5e1;
  border-top: 1px solid #dbe5ec;
  vertical-align: bottom;
  text-align: center;
  overflow: hidden;
  box-sizing: border-box;
}

.headerElem:nth-child(even) {
  background: #e1eaef;
}

/* Vertical Text Headers - Clean Native Rotation */
.header-content {
  height: 130px;
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
}

.header-text {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  max-height: 125px;
  letter-spacing: 0.2px;
  display: inline-block;
  line-height: 1.2;
}

.total-header-content {
  align-items: flex-start;
}

.total-header-text {
  font-weight: 800;
  font-size: 14px;
  color: #0f172a;
}

/* First Columns - Sticky Left Trio */
.first-column {
  background-color: #eef6fb;
  font-weight: 700;
  font-size: 14px;
  color: #1e293b;
  padding: 10px 12px;
  border-right: 1px solid #dbe5ec;
  border-bottom: 2px solid #cbd5e1;
  border-top: 1px solid #dbe5ec;
  vertical-align: bottom;
  box-sizing: border-box;
  overflow: hidden;
}

/* Account Column (0px) */
th.account-col {
  position: sticky;
  left: 0;
  z-index: 10;
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  text-align: left;
  background-color: #eef6fb;
}

td.account-cell {
  position: sticky;
  left: 0;
  z-index: 5;
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  text-align: left !important;
  font-size: 14px;
  font-weight: 700;
  background-color: #eef6fb !important;
  padding: 8px 12px;
  border-right: 1px solid #dbe5ec;
  border-bottom: 1px solid #dbe5ec;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-sizing: border-box;
}

.account-name-text {
  font-weight: 700;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

/* Created Column (left: 180px, width: 200px) */
th.created-col {
  position: sticky;
  left: 180px;
  z-index: 10;
  width: 200px;
  min-width: 200px;
  max-width: 200px;
  text-align: center;
  background-color: #eef6fb;
  white-space: nowrap;
}

td.created-cell {
  position: sticky;
  left: 180px;
  z-index: 5;
  width: 200px;
  min-width: 200px;
  max-width: 200px;
  text-align: center;
  background-color: #eef6fb !important;
  border-right: 1px solid #dbe5ec;
  border-bottom: 1px solid #dbe5ec;
  padding: 6px 8px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  box-sizing: border-box;
}

/* Stage Column (left: 380px, width: 180px) */
th.stage-col {
  position: sticky;
  left: 380px;
  z-index: 10;
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  text-align: center;
  background-color: #eef6fb;
  border-right: 2px solid #cbd5e1;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.06);
  padding: 10px 8px;
  overflow: hidden;
  box-sizing: border-box;
  white-space: nowrap;
}

td.stage-cell {
  position: sticky;
  left: 380px;
  z-index: 5;
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  text-align: center;
  background-color: #eef6fb !important;
  border-right: 2px solid #cbd5e1;
  border-bottom: 1px solid #dbe5ec;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.06);
  padding: 6px 8px;
  font-size: 13px;
  font-weight: 600;
  text-transform: capitalize;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-sizing: border-box;
}

.stage-cell-wrapper {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  box-sizing: border-box;
}

.stage-cell-text {
  display: block;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Subcategory Data Cells */
.subcategory-col {
  min-width: 44px;
  max-width: 44px;
  width: 44px;
  text-align: center;
}

td.subcategory-cell {
  min-width: 44px;
  max-width: 44px;
  width: 44px;
  text-align: center;
  padding: 6px 2px;
  border-right: 1px solid #dbe5ec;
  border-bottom: 1px solid #dbe5ec;
  box-sizing: border-box;
}

/* Alternating Column Backgrounds */
td:nth-child(even) {
  background: #f1f6f9;
}

td:nth-child(odd):not(.account-cell):not(.created-cell):not(.stage-cell):not(.total-data-cell) {
  background: #ffffff;
}

/* Total Column - Sticky Right */
th.total-header-cell {
  position: sticky;
  right: 0;
  z-index: 10;
  background-color: #eef6fb;
  min-width: 65px;
  max-width: 65px;
  width: 65px;
  border-left: 2px solid #cbd5e1;
  border-right: 1px solid #dbe5ec;
  border-top: 1px solid #dbe5ec;
  border-bottom: 2px solid #cbd5e1;
  box-shadow: -2px 0 4px rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
}

td.total-data-cell {
  position: sticky;
  right: 0;
  z-index: 5;
  background-color: #eef6fb !important;
  min-width: 65px;
  max-width: 65px;
  width: 65px;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  border-left: 2px solid #cbd5e1;
  border-right: 1px solid #dbe5ec;
  border-bottom: 1px solid #dbe5ec;
  box-shadow: -2px 0 4px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  box-sizing: border-box;
}

.total-count-text {
  font-weight: 800;
  color: #0f172a;
}

/* Common Cells */
td {
  padding: 6px;
  height: 42px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
}

.workspace-cell {
  font-weight: 600;
  cursor: pointer;
}

.count-cell {
  font-weight: 600;
  color: #1e293b;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  user-select: none;
}

.count-cell:hover {
  color: #1976d2;
  text-decoration: underline;
  background-color: #e0f2fe;
}

/* Footer / Total Row - Sticky Bottom */
tfoot {
  position: sticky;
  bottom: 0;
  z-index: 9;
}

.total-row {
  background-color: #eef6fb;
}

.total-row td {
  border-top: 2px solid #cbd5e1;
  border-bottom: 1px solid #dbe5ec;
  border-right: 1px solid #dbe5ec;
  font-weight: 700;
  background-color: #eef6fb;
  box-sizing: border-box;
}

.footer-account {
  position: sticky;
  left: 0;
  z-index: 11;
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  text-align: left !important;
  font-weight: 800 !important;
  background-color: #eef6fb !important;
  padding: 8px 12px;
}

.footer-created {
  position: sticky;
  left: 180px;
  z-index: 11;
  width: 200px;
  min-width: 200px;
  max-width: 200px;
  background-color: #eef6fb !important;
}

.footer-stage {
  position: sticky;
  left: 380px;
  z-index: 11;
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  background-color: #eef6fb !important;
  border-right: 2px solid #cbd5e1;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.06);
}

.footer-subitem {
  min-width: 44px;
  max-width: 44px;
  width: 44px;
  text-align: center;
}

.footer-count-text {
  font-weight: 700;
  cursor: pointer;
}

.footer-count-text:hover {
  color: #1976d2;
  text-decoration: underline;
}

.footer-grand-total {
  position: sticky;
  right: 0;
  z-index: 11;
  width: 65px;
  min-width: 65px;
  max-width: 65px;
  background-color: #eef6fb !important;
  border-left: 2px solid #cbd5e1 !important;
  box-shadow: -2px 0 4px rgba(0, 0, 0, 0.06);
  text-align: center;
}

.footer-grand-total-text {
  font-weight: 800;
  font-size: 15px;
  color: #0f172a;
  cursor: pointer;
}

/* Table Card Wrapper & Loader Overlay */
.table-card-wrapper {
  position: relative;
  width: 100%;
}

.table-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  min-height: 360px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(2px);
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  pointer-events: all;
}

.table-loader-text {
  font-size: 14px;
  font-weight: 600;
  color: #1e40af;
  letter-spacing: 0.3px;
}

.no-data-cell {
  text-align: center;
  padding: 48px 16px !important;
  background: #ffffff !important;
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.no-data-title {
  font-size: 15px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
}

.no-data-subtitle {
  font-size: 13px;
  color: #94a3b8;
}
</style>
